
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface GuestAvailability {
  invite_id: string;
  display_name: string;
  rsvp_status: string;
  avatar_url?: string | null;
  user_full_name?: string | null;
  guest_avatar_url?: string | null;
}

export const useEventAvailability = (eventId: string) => {
  return useQuery({
    queryKey: ['event-availability', eventId],
    queryFn: async () => {
      // Fetch all guest availability data from event_invites
      const { data: invitesData, error: invitesError } = await supabase
        .from('event_invites')
        .select(`
          invite_id,
          display_name,
          rsvp_status,
          selected_dates,
          selected_times
        `)
        .eq('event_id', eventId)
        .in('rsvp_status', ['accepted', 'maybe']);

      if (invitesError) {
        console.error('Error fetching invites availability:', invitesError);
        throw invitesError;
      }

      // Process the data to create a date-to-time-to-guests mapping
      const dateTimeAvailability: Record<string, Record<string, string[]>> = {};
      
      invitesData.forEach(invite => {
        // Skip if no selected dates
        if (!invite.selected_dates || invite.selected_dates.length === 0) return;
        
        // Process each selected date
        invite.selected_dates.forEach(date => {
          if (!dateTimeAvailability[date]) {
            dateTimeAvailability[date] = {
              morning: [],
              afternoon: [],
              evening: []
            };
          }
          
          // If guest has specific time preferences
          if (invite.selected_times && invite.selected_times.length > 0) {
            invite.selected_times.forEach(timeOfDay => {
              if (['morning', 'afternoon', 'evening'].includes(timeOfDay)) {
                if (!dateTimeAvailability[date][timeOfDay].includes(invite.invite_id)) {
                  dateTimeAvailability[date][timeOfDay].push(invite.invite_id);
                }
              }
            });
          } else {
            // If no specific time preferences, add to all time slots
            ['morning', 'afternoon', 'evening'].forEach(timeOfDay => {
              if (!dateTimeAvailability[date][timeOfDay].includes(invite.invite_id)) {
                dateTimeAvailability[date][timeOfDay].push(invite.invite_id);
              }
            });
          }
        });
      });

      return dateTimeAvailability;
    },
    staleTime: 5 * 60 * 1000, // 5 minute cache
  });
};
